import $ from 'jquery';
import { unstable_batchedUpdates } from 'react-dom';

import {
  AlertOptions,
  PanelSupplier,
  ProblemData,
  cancelReadAloud,
  closeAllProblemDialogs,
} from '@gmm/problem';

import { MIN_LANDSCAPE_WIDTH, MIN_PORTRAIT_WIDTH } from '../../cnusr/constants';
import {
  LoginRequest,
  alerts,
  login,
  postHandInExam,
  postIncrementTimeSeen,
} from '../api';
import { apiQueue } from '../api/queue';
import { receiveProblem } from '../api/responseHandlerShared';
import { changeHandStatus } from '../api/sendRaisedHand/util';
import { getType, getWorkProgress } from '../hooks/useProcessedWorkStore';
import {
  activity,
  resetActivityMonitor,
  turnOffInactiveTimer,
  turnOffPingTimers,
  turnOffSmileyTimers,
} from '../legacy';
import { bannerState, problemState } from '../stores';
import {
  getGmm,
  getLastMsgId,
  getTimeSeenStart,
  setTimeSeenStart,
} from '../stores/globalState';
import { problemJsonMap } from '../stores/problemJsonMap';
import {
  SHOW_ANSWER,
  studentAppModalState,
} from '../stores/studentAppModalStore';
import { userState } from '../stores/userStore';
import { workState } from '../stores/workStore';
import { NO_WORK, hasExamWorkType } from '../types';

import { getTimeStampWithMillis } from './getTimestamp';

const logs = new Array<string>();
const MAX_LOG_LINES = 100;

/*
 * Replacing a problem is a two-stage operation:
 *
 * 1. showAnswer() stores the server-provided replacement while the student is
 *    still reading the answer/help modal.
 * 2. animateReplaceProblem() animates the old problem away and then moves the
 *    stored replacement into the live problem state.
 *
 * The animation is only a visual effect. The state change must still finish if
 * responsive layout changes remove the animated DOM element.
 */
const REPLACE_PROBLEM_ANIMATION_DURATION_MS = 600;
// Wait slightly longer than the CSS transition so transitionend normally wins,
// while still guaranteeing that application state eventually advances.
const REPLACE_PROBLEM_ANIMATION_FALLBACK_MS =
  REPLACE_PROBLEM_ANIMATION_DURATION_MS + 100;
// A responsive remount can remove the old wrapper without firing a transition
// event. Check often enough to avoid leaving the replacement pending while the
// newly mounted problem is visible, without continuously observing the DOM.
const REPLACE_PROBLEM_DETACH_CHECK_MS = 50;
// The jQuery namespace lets this code remove only its own transition handlers.
const REPLACE_PROBLEM_TRANSITION_EVENTS =
  'transitionend.replaceProblem transitioncancel.replaceProblem';

// This is an imperative handle to the animation currently in progress. A new
// answer or animation can use it to finish the old handoff before reusing the
// single replacementProblem store slot.
let finishActiveProblemReplacementAnimation: (() => void) | undefined;

interface LoginInteral {
  showSmileys?: boolean;
  switchClasId?: number;
  alertOptions?: AlertOptions;
}

export function loginInternal({
  showSmileys,
  switchClasId,
  alertOptions,
}: LoginInteral = {}): void {
  studentAppModalState().setLoading(true, 'login');

  unstable_batchedUpdates(() => {
    apiQueue.clear();
    workState().setCurrentWork(NO_WORK);
    studentAppModalState().clear();
    problemState().clear();
    problemJsonMap.clear();
    getGmm()?.setToBlank();
    studentAppModalState().closeAll();
    turnOffAllTimers();
    resetActivityMonitor();
    bannerState().setDailyGoal(0);

    if (bannerState().isHandRaised || bannerState().handRaisedProblemId) {
      changeHandStatus(false);
    }

    const packet: LoginRequest = {
      username: userState().userName || '',
      password: userState().password || '',
      loginInternal: true,
      ss: userState().redisSessionId,
      lastMsgId: getLastMsgId(),
      // prevent cache
      a: new Date().getTime(),
    };

    if (switchClasId) packet.switchClasId = switchClasId;
    login(packet, showSmileys);

    if (alertOptions) gmmAlert(alertOptions);
  });
}

export function harvestTimeSeen(send?: boolean): number | void {
  const timeSeenStart = getTimeSeenStart();

  if (!timeSeenStart) {
    setTimeSeenStart(new Date());

    return;
  }

  const delta = Math.max(0, new Date().getTime() - timeSeenStart.getTime());

  setTimeSeenStart(new Date());

  if (send && delta > 1500) {
    postIncrementTimeSeen({
      problemId: problemState().selectedID,
      increment: delta,
    });
  }

  return delta;
}

export const handleSevereError = (msg: string): void => {
  studentAppModalState().setLoading(false, 'nuclear');

  gmmAlert(alerts.severeError(msg));

  return;
};

export function reboot(msg: string): void {
  logHistory(
    `Rebooting (return to login after msg dialog closes) | msg: ${msg}`
  );

  gmmAlert({
    msg,
    reload: true,
    top: 'GMM',
  });

  studentAppModalState().setLoading(false, 'nuclear');
  studentAppModalState().setExamLoading(false);
  turnOffAllTimers();
  apiQueue.clear();

  return;
}

export function turnOffAllTimers(): void {
  turnOffPingTimers();
  turnOffInactiveTimer();
  turnOffSmileyTimers();
}

export function logHistory(str: string): void {
  logs.push(`${getTimeStampWithMillis()} ${str}`);
}

export function logWork(pre: string): void {
  const work = workState().currentWork;

  logHistory(
    `${pre} 
    Work Type: ${getType(work)}  
    Work Name: ${work.name}  
    Work ID: ${work.workId}  
    Progress: ${getWorkProgress(work)}`
  );
}

export function logRestoreIds(pre: string): void {
  const restoreIds = Object.keys(problemState().problems).join(', ');

  logHistory(`${pre} Restore Ids: ${restoreIds}`);
}

export function getHistory(): string {
  const recentLogs = logs.slice(-MAX_LOG_LINES);

  return recentLogs.join('\n');
}

export function processAjaxFailure(jqXhr: JQuery.jqXHR<any>): boolean {
  switch (jqXhr.status) {
    case 401: {
      reboot('Session Expired');

      return true;
    }

    default: {
      return false;
    }
  }
}

interface ReplaceProblem {
  id: number;
  p: ProblemData;
}

export interface ShowAnswer {
  // json for display of answer to current problem on showAnswerModal
  answerJson?: ProblemData;
  // Json for helpful steps and answer. Server sends EITHER answerJson or help.
  help?: PanelSupplier;

  // Use this to replace current problem when student is done looking at answer.
  replaceProblem: ReplaceProblem;

  replacementsUsedToday: number;
}

export function showAnswer(dto: ShowAnswer): void {
  const { answerJson, replaceProblem, replacementsUsedToday } = dto;

  if ((!answerJson || !replaceProblem) && !dto.help) return;

  // replacementProblem is a single shared slot. Finish any older animation
  // before writing into that slot, or its delayed callback could consume the
  // new replacement instead of the one it originally animated.
  finishActiveProblemReplacementAnimation?.();

  bannerState().setReplacementsUsedToday(replacementsUsedToday);

  // Stage the next problem without displaying it yet. Closing the answer/help
  // modal calls animateReplaceProblem(), which performs the actual handoff.
  studentAppModalState().setReplacementProblem({
    id: replaceProblem.id,
    problem: replaceProblem.p,
    performingTargetedOverride: true,
  });

  studentAppModalState().setAnswerToCurrentProblemOrHelp(dto);

  studentAppModalState().setCurrentModal(SHOW_ANSWER);
}

/**
 * Move the staged replacement into the live problem state.
 *
 * Every completion route (transition end, transition cancellation, missing DOM,
 * or timeout) comes through this function, so it must be safe to call more than
 * once for the same replacement.
 */
function commitPendingReplacementProblem(): void {
  const problem = studentAppModalState().replacementProblem;

  // Another completion signal may already have committed this replacement.
  if (!problem) return;

  // Clear the shared slot before updating other stores. This both makes the
  // operation idempotent and prevents reentrant store updates from finding and
  // committing the same replacement again.
  studentAppModalState().setReplacementProblem(undefined);

  // A replacement counts the previous problem as completed, so reset its square
  // status/penalties before installing the replacement payload as the live one.
  problemState().resetSquare(`${problem.id}`);
  receiveProblem(problem);
}

/**
 * Animate the current problem out, then commit the replacement staged by
 * showAnswer(). State correctness takes priority over the visual transition:
 * when the wrapper is missing, canceled, or remounted, another completion path
 * still performs the handoff.
 */
export function animateReplaceProblem(): void {
  // Only one replacement animation may own the shared staged problem. Settling
  // the previous animation also removes its event handlers and fallback timer.
  finishActiveProblemReplacementAnimation?.();

  const $canvas = $('.supports-animated-replacement');

  // Some layouts do not render an animatable wrapper. The state handoff is still
  // required, so skip only the visual effect.
  if (!$canvas.length) {
    commitPendingReplacementProblem();

    return;
  }

  // transitionend, transitioncancel, and the timeout can race. This guard makes
  // the first signal the winner and turns all later signals into no-ops.
  let finished = false;

  // Holder objects can safely exist before the browser assigns timer ids,
  // allowing finish() to run from any completion path without referencing an
  // undeclared timer.
  const completionTimers: { detachCheckId?: number; fallbackId?: number } = {};

  const finish = (): void => {
    if (finished) return;

    finished = true;

    // Whichever path won should clean up the other possible completion paths.
    if (completionTimers.detachCheckId !== undefined) {
      window.clearInterval(completionTimers.detachCheckId);
    }

    if (completionTimers.fallbackId !== undefined) {
      window.clearTimeout(completionTimers.fallbackId);
    }

    $canvas.off(REPLACE_PROBLEM_TRANSITION_EVENTS, handleTransitionCompletion);

    // Do not clear a newer animation's handle if it replaced this one while
    // store updates were running.
    if (finishActiveProblemReplacementAnimation === finish) {
      finishActiveProblemReplacementAnimation = undefined;
    }

    // The state handoff happens exactly once; resetting the transform is only
    // visual cleanup for wrappers that are still mounted.
    commitPendingReplacementProblem();
    $canvas.css('transform', 'translateX(0%) rotateY(0deg)');
  };

  const handleTransitionCompletion = (event: JQuery.TriggeredEvent): void => {
    // Child controls may have their own CSS transitions whose events bubble to
    // a wrapper. Accept any wrapper captured for this animation, but do not let
    // a descendant's unrelated transition complete the problem replacement.
    if ($canvas.is(event.target as Element)) finish();
  };

  // Expose this animation's single cleanup/commit path to showAnswer() and to a
  // later animateReplaceProblem() call.
  finishActiveProblemReplacementAnimation = finish;

  // Remove a handler left by an interrupted invocation, then attach both normal
  // and cancellation completion events under this feature's namespace.
  $canvas
    .off(REPLACE_PROBLEM_TRANSITION_EVENTS)
    .on(REPLACE_PROBLEM_TRANSITION_EVENTS, handleTransitionCompletion);

  // Rotate and slide the current problem halfway out of view. finish() restores
  // the transform after the replacement has become the live problem.
  $canvas.css({
    transition: `transform ${REPLACE_PROBLEM_ANIMATION_DURATION_MS}ms`,
    'transform-style': 'preserve-3d',
    transform: 'translateX(-50%) rotateY(90deg)',
  });

  // Browsers do not reliably emit transitioncancel when responsive rendering
  // removes a wrapper. Detect that remount promptly so the replacement becomes
  // live before a newly mounted wrapper can display the stale problem.
  const animatedElements = $canvas.toArray();

  completionTimers.detachCheckId = window.setInterval(() => {
    if (animatedElements.some(element => !element.isConnected)) finish();
  }, REPLACE_PROBLEM_DETACH_CHECK_MS);

  // The final timeout covers every remaining failure mode, including a browser
  // that emits no transition event while leaving the wrapper mounted.
  completionTimers.fallbackId = window.setTimeout(
    finish,
    REPLACE_PROBLEM_ANIMATION_FALLBACK_MS
  );
}

export function gmmAlert({
  msg,
  top = '',
  style = 'green',
  reload = false,
  reloadIsInternal = false,
  removeWhenAnotherDialogShows = false,
  removeProblemRelatedDialogs = false,
  problemRelated = false,
  killDuplicates = false,
}: AlertOptions): void {
  cancelReadAloud();

  if (msg.indexOf('Invalid:') > -1 && (!top || top === 'Message')) {
    const i = msg.indexOf('Invalid:');

    msg = msg.substring(i + 9);
    top = 'Invalid';
  } else if (msg.indexOf('FYI:') > -1 && (!top || top === 'Message')) {
    const i = msg.indexOf('FYI:');

    msg = msg.substring(i + 5);
    top = 'FYI';
  }

  if (!top || top === '') top = 'GMM';

  if (removeProblemRelatedDialogs) {
    // problem-related dialogs owned by student app
    studentAppModalState().closeAllProblemDialogs();
    // problem-related dialogs owned by @gmm/problem package
    closeAllProblemDialogs();
  }

  studentAppModalState().addStackableDialog({
    msg,
    style,
    top,
    reload,
    reloadIsInternal,
    removeWhenAnotherDialogShows,
    problemRelated,
    killDuplicates,
  });

  if (reload) {
    turnOffAllTimers();
    setTimeout(window.location.reload, 10 * 60000);
  }
}

export async function submitTest(handIn?: boolean): Promise<void> {
  activity();

  const work = workState().currentWork;

  if (!hasExamWorkType(work)) return;

  if (handIn && typeof work.sitId === 'number')
    await postHandInExam(work.sitId);

  loginInternal();
}

// We have this sort of info from useLayout, but sometimes need it outside of
// React components.
export const isMobile = (): boolean => {
  return window.innerWidth < MIN_PORTRAIT_WIDTH;
};

export const isLandscape = (): boolean => {
  return window.innerWidth >= MIN_LANDSCAPE_WIDTH;
};

export const allowGames = (): boolean => {
  const hasGames = studentAppModalState().availableGames.length > 0;
  const { blockGames, blockGamesClass } = bannerState();

  return hasGames && !blockGames && !blockGamesClass;
};

export const packageGenericError = (err: any): string => {
  if (err instanceof Error)
    return `
    message: ${err.message || 'No message'}
    stack: ${err.stack || 'No stack'}`;

  return JSON.stringify(err, null, 2);
};
